import { useEffect, type ReactNode } from "react";
import {
  AccessibilityInfo,
  ActivityIndicator,
  Platform,
  StyleSheet,
  Text,
  View,
  type TextStyle,
} from "react-native";
import { useTranslation } from "react-i18next";
import type { Program } from "@heraion/shared";

import type { UiTheme } from "../theme";
import { FilterChipsRow } from "../ExercisePicker/FilterChipsRow";
import { EmptyState } from "../primitives/EmptyState";
import { LibraryFiltersPanel } from "./LibraryFilters";
import { filtersForView } from "./libraryFilters";
import type { LibraryItem } from "./useLibrary";
import type { useLibraryBrowse } from "./useLibraryBrowse";

// The Library browser's render half (split out of LibraryBrowser.tsx to
// keep that file layout+hosting only — see useLibraryBrowse.ts for the
// derivation half it renders). Zero fetch, zero state of its own: every
// value here is either a derived prop from useLibraryBrowse or a resolved
// banner string, and the row itself is injected via `renderItem` so this
// file never has to know about LibraryItemCard or useLibrary.

// CN-libraryresults-wide-seam — this file's prop list had grown to 21
// (next/consolidation.md), one flat pile mixing three unrelated shapes: the
// resolved banner strings, the whole of useLibraryBrowse's return value, and
// the handful of props that belong to neither. Collapsed into two objects
// along that fault line; PURE GROUPING, no prop dropped or renamed and no
// behaviour changed. `browse` reuses useLibraryBrowse's own return type
// instead of re-declaring its twelve fields a second time.
// Locale, non esportato: i due callsite costruiscono l'oggetto inline e
// nessuno fuori da qui ne ha bisogno. Un tipo esportato che nessuno importa
// è superficie pubblica morta (knip lo segnalava già la notte stessa in cui
// è nato, 20/09).
type LibraryResultsBanners = {
  cloneFlash: string | null;
  cloneError: string | null;
  actionError: string | null;
  /** CO-22 — the standalone AssignSheet's post-save confirmation
   *  (useLibraryAssignSheet). Same reason as `cloneFlash`: the sheet that
   *  triggered it is already gone by the time this renders. Optional,
   *  ⇒ null — ProgramPickerSheet (this file's other consumer) confirms
   *  its own single-target assign through its `notify` seam instead. */
  assignFlash?: string | null;
};

export type LibraryResultsProps = {
  theme: UiTheme;
  wide: boolean;
  authoredAs: Program["authoredAs"] | null;
  banners: LibraryResultsBanners;
  browse: ReturnType<typeof useLibraryBrowse>;
  renderItem: (item: LibraryItem) => ReactNode;
  /** CO-23 — false hides the FilterChipsRow entirely, for the coach's program
   *  picker whose tab is locked to "personal" (useLibraryBrowse's
   *  `lockedFilter`): a chip row with nothing to switch to is dead chrome.
   *  Defaults to true so the browser (LibraryBrowser, the only other caller)
   *  renders byte-identically. */
  showTabs?: boolean;
};

// A flash confirmation can be the ONLY sign of where something actually
// landed (CO-22 review: `assignFlash` literally says "look elsewhere, it's
// in your inbox now" — the one message someone not looking at the screen
// right now must still receive). `accessibilityLiveRegion` announces a Text
// change on Android + web (r-n-w maps it to aria-live) but is inert on iOS,
// so the same message is also pushed through `announceForAccessibility`,
// gated to iOS only so Android doesn't hear it twice — same split as
// Auth/ForgotPasswordForm.tsx. One shared spot: `cloneFlash` and
// `assignFlash` are the two real callsites, not two copies of the same fix.
function FlashText({ style, children }: { style: TextStyle; children: string }) {
  useEffect(() => {
    if (Platform.OS === "ios") AccessibilityInfo.announceForAccessibility(children);
  }, [children]);
  return (
    <Text style={style} accessibilityLiveRegion="polite" accessibilityRole="alert">
      {children}
    </Text>
  );
}

export function LibraryResults({
  theme,
  wide,
  authoredAs,
  banners,
  browse,
  renderItem,
  showTabs = true,
}: LibraryResultsProps) {
  const {
    filter,
    activeFilterSet,
    onPickFilter,
    filterLabel,
    tagFilters,
    setTagFilters,
    filterOptions,
    loading,
    loadFailed,
    filteredOut,
    hasItems,
    sections,
  } = browse;
  const { cloneFlash, cloneError, actionError, assignFlash = null } = banners;
  const { t } = useTranslation();
  const styles = makeStyles(theme);

  return (
    <View style={styles.wrap}>
      {showTabs ? (
        <FilterChipsRow
          theme={theme}
          label={t("library.header", { defaultValue: "Browse" })}
          options={filtersForView(authoredAs)}
          active={activeFilterSet}
          onToggle={onPickFilter}
          renderLabel={filterLabel}
          flush
        />
      ) : null}

      <LibraryFiltersPanel
        theme={theme}
        filters={tagFilters}
        onChange={setTagFilters}
        options={filterOptions}
      />

      {cloneFlash ? <FlashText style={styles.flash}>{cloneFlash}</FlashText> : null}
      {cloneError ? <Text style={styles.error}>{cloneError}</Text> : null}
      {actionError ? <Text style={styles.error}>{actionError}</Text> : null}
      {assignFlash ? <FlashText style={styles.flash}>{assignFlash}</FlashText> : null}

      {loading ? (
        <View style={styles.center}>
          <ActivityIndicator color={theme.palette.ink2} />
          <Text style={styles.muted}>
            {t("library.loading", { defaultValue: "Loading the library…" })}
          </Text>
        </View>
      ) : loadFailed ? (
        <Text style={styles.error}>
          {t("library.errLoad", {
            defaultValue: "Could not load the library.",
          })}
        </Text>
      ) : filteredOut ? (
        <EmptyState
          theme={theme}
          icon="search"
          title={t("library.noResultsTitle", { defaultValue: "No matches" })}
          sub={t("library.noResultsSub", {
            defaultValue: "Adjust the search or the filters.",
          })}
        />
      ) : !hasItems && filter === "favorites" ? (
        <EmptyState
          theme={theme}
          icon="star"
          title={t("library.emptyFavoritesTitle", {
            defaultValue: "No favorites yet",
          })}
          sub={t("library.emptyFavoritesSub", {
            defaultValue: "Star a program or workout to keep it up here.",
          })}
        />
      ) : !hasItems && filter === "coach" ? (
        <EmptyState
          theme={theme}
          icon="user"
          title={t("library.emptyCoachTitle", {
            defaultValue: "Nothing assigned yet",
          })}
          sub={t("library.emptyCoachSub", {
            defaultValue: "No one has assigned you a program or workout yet.",
          })}
        />
      ) : !hasItems ? (
        <EmptyState
          theme={theme}
          title={t("library.emptyTitle", { defaultValue: "Nothing here yet" })}
          sub={t("library.emptySub", {
            defaultValue:
              "Programs and workouts you create or clone will show up here.",
          })}
        />
      ) : (
        sections.map((section) =>
          section.items.length === 0 ? null : (
            <View key={section.key} style={styles.section}>
              <Text style={styles.sectionLabel}>{section.label}</Text>
              <View style={wide ? styles.grid : styles.list}>
                {section.items.map((item) => (
                  <View key={item.key} style={wide ? styles.gridCell : undefined}>
                    {renderItem(item)}
                  </View>
                ))}
              </View>
            </View>
          ),
        )
      )}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: t } = theme;
  return StyleSheet.create({
    wrap: {
      gap: s.s3,
    },
    section: {
      gap: s.s2,
    },
    sectionLabel: {
      color: p.ink3,
      fontSize: t.sizes.xs,
      fontWeight: "700",
      letterSpacing: 0.6,
      textTransform: "uppercase",
    },
    list: {
      gap: s.s3,
    },
    grid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: s.s3,
    },
    gridCell: {
      flexGrow: 1,
      flexBasis: "48%",
    },
    center: {
      alignItems: "center",
      gap: s.s2,
      paddingVertical: s.s5,
    },
    muted: {
      color: p.ink3,
      fontSize: t.sizes.sm,
    },
    // Same voice as the TemplatesShelf clone flash (brand, not a status
    // colour — it's a confirmation, not a warning).
    flash: {
      color: p.brand500,
      fontSize: t.sizes.sm,
      fontWeight: "600",
    },
    error: {
      color: p.danger,
      fontSize: t.sizes.sm,
      fontWeight: "600",
    },
  });
}
